import { useState, useEffect } from "react";
import http from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Clock, CheckCircle } from "lucide-react";
import Layout from "@/components/Layout";

const serviceTypes = [
  { value: "kaffemaskin", label: "Kaffemaskin" },
  { value: "symaskin", label: "Symaskin" },
  { value: "storkjokken", label: "Storkjøkkenmaskin" },
  { value: "kontormaskin", label: "Kontormaskin" },
  { value: "annet", label: "Annet" },
];

export default function Booking() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", service_type: "", machine_brand: "", machine_model: "", preferred_date: "", preferred_time: "", message: "", is_business: false, priority: "normal" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [slots, setSlots] = useState([]);
  const [dayInfo, setDayInfo] = useState(null);

  useEffect(() => {
    if (!formData.preferred_date) { setSlots([]); setDayInfo(null); return; }
    let active = true;
    (async () => {
      try {
        const { data } = await http.get(`/v1/public/availability?date=${formData.preferred_date}`);
        if (!active) return;
        setDayInfo(data);
        setSlots(data.open ? data.slots : []);
        setFormData((f) => ((data.slots || []).some((s) => s.time === f.preferred_time) ? f : { ...f, preferred_time: "" }));
      } catch {
        if (active) { setSlots([]); setDayInfo(null); }
      }
    })();
    return () => { active = false; };
  }, [formData.preferred_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.preferred_date || !formData.preferred_time) {
      toast.error("Vennligst fyll ut alle obligatoriske felt.");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/v1/bookings", { ...formData, status: "pending" });
      setSubmitted(true);
      toast.success("Forespørselen er mottatt. Vi kontakter deg for å bekrefte tidspunktet.");
    } catch {
      toast.error("Noe gikk galt. Vennligst prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return t.toISOString().split("T")[0];
  };

  if (submitted) {
    return (
      <Layout>
        <section className="py-16 md:py-24 bg-[#FFF8F0]">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6"><CheckCircle className="h-8 w-8 text-green-600" /></div>
            <h2 className="text-2xl font-bold text-[#3D2314] mb-4 font-display">Booking mottatt!</h2>
            <p className="text-[#1A1A1A]/70 mb-6">Vi har mottatt din booking og vil kontakte deg for å bekrefte tidspunktet.</p>
            <Button onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", service_type: "", machine_brand: "", machine_model: "", preferred_date: "", preferred_time: "", message: "", is_business: false, priority: "normal" }); }} className="bg-[#3D2314] hover:bg-[#3D2314]/90 text-white">Ny booking</Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-[#3D2314] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 font-display">Bestill time</h1>
          <p className="text-white/80 max-w-2xl mx-auto">Velg en dato og tid som passer deg, så tar vi imot utstyret ditt til avtalt tidspunkt.</p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#FFF8F0]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-[#D4A574]/30 shadow-lg">
            <CardHeader><CardTitle className="text-xl text-[#3D2314] flex items-center gap-2"><CalendarDays className="h-5 w-5" />Bookingskjema</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-[#3D2314] font-medium">Type utstyr *</Label>
                  <Select value={formData.service_type} onValueChange={(v) => setFormData({ ...formData, service_type: v })}>
                    <SelectTrigger className="border-[#D4A574]/30" data-testid="booking-service"><SelectValue placeholder="Velg type utstyr" /></SelectTrigger>
                    <SelectContent>{serviceTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-[#3D2314] font-medium">Merke (hvis kjent)</Label><Input value={formData.machine_brand} onChange={(e) => setFormData({ ...formData, machine_brand: e.target.value })} placeholder="f.eks. Jura, DeLonghi" className="border-[#D4A574]/30" data-testid="booking-brand" /></div>
                  <div className="space-y-2"><Label className="text-[#3D2314] font-medium">Modell (hvis kjent)</Label><Input value={formData.machine_model} onChange={(e) => setFormData({ ...formData, machine_model: e.target.value })} placeholder="f.eks. E8, Magnifica" className="border-[#D4A574]/30" data-testid="booking-model" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-[#3D2314] font-medium">Ønsket dato *</Label><Input type="date" min={getMinDate()} value={formData.preferred_date} onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })} className="border-[#D4A574]/30" data-testid="booking-date" /></div>
                  <div className="space-y-2">
                    <Label className="text-[#3D2314] font-medium flex items-center gap-1"><Clock className="h-4 w-4" />Innleveringstid *</Label>
                    <Select value={formData.preferred_time} onValueChange={(v) => setFormData({ ...formData, preferred_time: v })} disabled={!formData.preferred_date || !dayInfo?.open}>
                      <SelectTrigger className="border-[#D4A574]/30" data-testid="booking-time"><SelectValue placeholder={!formData.preferred_date ? "Velg dato først" : (dayInfo && !dayInfo.open ? "Stengt denne dagen" : "Velg tid")} /></SelectTrigger>
                      <SelectContent>{slots.map((s) => <SelectItem key={s.time} value={s.time}>{s.time}{s.by_arrangement ? " (etter avtale)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                    {dayInfo && !dayInfo.open && <p className="text-xs text-red-600" data-testid="booking-day-closed">{dayInfo.reason}</p>}
                    {dayInfo?.open && slots.length === 0 && <p className="text-xs text-red-600">Ingen ledige tider denne dagen.</p>}
                  </div>
                </div>
                <div className="rounded-lg bg-[#FFF3E6] border border-[#D4A574]/40 p-3 text-xs text-[#3D2314]/80" data-testid="innlevering-note">
                  Tiden du bestiller gjelder <strong>kun innlevering</strong> av maskinen – dette er <strong>ikke</strong> estimert reparasjonstid. Reparasjonstid avhenger blant annet av feilsøking, nødvendig arbeid og eventuell levering av reservedeler.
                </div>
                <div className="space-y-4">
                  <div className="space-y-2"><Label className="text-[#3D2314] font-medium">Navn *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ditt fulle navn" className="border-[#D4A574]/30" data-testid="booking-name" /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-[#3D2314] font-medium">E-post *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="din@epost.no" className="border-[#D4A574]/30" data-testid="booking-email" /></div>
                    <div className="space-y-2"><Label className="text-[#3D2314] font-medium">Telefon *</Label><Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="XXX XX XXX" className="border-[#D4A574]/30" data-testid="booking-phone" /></div>
                  </div>
                </div>
                <div className="space-y-2"><Label className="text-[#3D2314] font-medium">Melding (valgfritt)</Label><Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Beskriv kort hva som er problemet med utstyret..." rows={4} className="border-[#D4A574]/30" /></div>

                <div className="rounded-xl border border-[#D4A574]/30 bg-[#FFF8F0] p-4 space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="booking-business">
                    <input type="checkbox" checked={formData.is_business} onChange={(e) => setFormData({ ...formData, is_business: e.target.checked, priority: e.target.checked ? formData.priority : "normal" })} className="h-4 w-4 accent-[#3D2314]" />
                    <span className="text-sm font-medium text-[#3D2314]">Jeg er bedriftskunde</span>
                  </label>
                  {formData.is_business && (
                    <label className="flex items-center gap-3 cursor-pointer" data-testid="booking-urgent">
                      <input type="checkbox" checked={formData.priority === "akutt"} onChange={(e) => setFormData({ ...formData, priority: e.target.checked ? "akutt" : "normal" })} className="h-4 w-4 accent-red-600" />
                      <span className="text-sm font-medium text-[#3D2314]">Utrykning / Haster – vi trenger rask hjelp</span>
                    </label>
                  )}
                  <p className="text-xs text-[#1A1A1A]/60">Hastesaker fra bedrifter prioriteres. Vi tar kontakt så raskt som mulig for å avtale utrykning.</p>
                </div>

                <Button type="submit" disabled={submitting} data-testid="booking-submit" className="w-full bg-[#D4A574] hover:bg-[#D4A574]/90 text-[#3D2314] font-semibold text-base py-3">{submitting ? "Sender..." : "Bekreft booking"}</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
